import { useCallback, useEffect, useState } from "react";

type State<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
};

/**
 * Carga datos y expone un `reload` para refrescarlos tras escribir.
 * `deps` se pasa tal cual a useEffect.
 */
export function useAsync<T>(
  load: () => Promise<T>,
  deps: unknown[] = [],
): State<T> & { reload: () => void; setData: (data: T) => void } {
  const [state, setState] = useState<State<T>>({
    data: null,
    error: null,
    loading: true,
  });
  const [tick, setTick] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(load, deps);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    run()
      .then((data) => {
        if (!cancelled) setState({ data, error: null, loading: false });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          data: null,
          error: err instanceof Error ? err.message : "Error al cargar",
          loading: false,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [run, tick]);

  return {
    ...state,
    reload: () => setTick((t) => t + 1),
    setData: (data: T) => setState({ data, error: null, loading: false }),
  };
}
